import * as turf from '@turf/turf';
import type { ArrowObject } from '~/common/types';
import { BRIGHT_COLORS } from '~/constants/color';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject from '../VertexObject';
import { getArrowPath, getArrowScale, STEM_LENGTH, ZOOM_THRESHOLD } from './common';

export default class EditArrowObject extends CanvasObject {
  dragOffset: Point = { x: NaN, y: NaN };
  vertices: VertexObject[] = [];
  _onArrowUpdated: (p: LngLat[]) => void;
  pointer: Path2D;
  lineAngle: number;

  constructor(id: string, surface: SurfaceBase, onArrowUpdated: (p: LngLat[]) => void) {
    super(id, surface);
    this.interactive = true;
    this._onArrowUpdated = onArrowUpdated;
    this.pointer = getArrowPath();

    this.createVertices();
    this.lineAngle = this.computeLineAngle();

    this.on('dragStart', () => {
      this.vertices.forEach((v) => {
        v.visible = false;
      });
      this.dragOffset = { x: 0, y: 0 };
      this.resetDragOffset();
      this.renderAll();
    });

    this.on('dragging', (e) => {
      this.dragOffset = e.diff();
      this.renderAll();
    });

    this.on('dragEnd', () => {
      this.vertices.forEach((v) => {
        v.point.x += this.dragOffset.x;
        v.point.y += this.dragOffset.y;
        v.refreshMapCoords();
        v.visible = true;
      });

      this.resetDragOffset();
      this.onArrowUpdated();
      this.renderAll();
    });
  }

  getCurrentArrowObject(): ArrowObject | undefined {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'arrow') return;
    return currentObject;
  }

  createVertices = () => {
    const arrow = this.getCurrentArrowObject();
    if (!arrow) return;
    arrow.geometry.coordinates.forEach((c, i) => {
      const lngLat = { lng: c[0], lat: c[1] };
      const point = this.surface.toScreenCoords(lngLat);

      const v = new VertexObject(`arrow-v-${i}`, this.surface, {
        point,
        lngLat,
      });

      v.interactive = true;

      v.on('dragging', (e) => {
        v.point = e.currentPoint;
        v.lngLat = e.currentLngLat;

        this.lineAngle = this.computeLineAngle();
        this.renderAll();
      });

      v.on('dragEnd', () => this.onArrowUpdated());

      this.vertices.push(v);
      this.addChild(v);
    });
  };

  computeLineAngle = (): number => {
    if (this.vertices.length === 0) return 0;
    const p1 = this.vertices[0].point;
    const p2 = this.vertices[1].point;

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    const a = -1 * Math.atan2(dx, dy) + Math.PI;
    return a;
  };

  onArrowUpdated = () => {
    this._onArrowUpdated([...this.vertices].map((v) => v.lngLat));
  };

  isDragging = () => {
    return !Number.isNaN(this.dragOffset.x);
  };

  computeAnchorPoint = () => {
    const zoom = this.surface.map.getZoom();
    let scale = 1;

    const p1 = this.vertices[0].point;
    const p2 = this.vertices[1].point;

    const a = { x: p1.x, y: p1.y };

    if (zoom <= ZOOM_THRESHOLD) {
      scale = getArrowScale(zoom);
    }

    if (p1.x <= p2.x) {
      a.x = a.x - STEM_LENGTH * scale;
    } else {
      a.x = a.x + STEM_LENGTH * scale;
    }

    return a;
  };

  resetDragOffset = () => {
    this.dragOffset = { x: NaN, y: NaN };
  };

  getBufferedPolygonCoords = (arrow: ArrowObject) => {
    const { subtype } = arrow.properties;
    if (subtype === 'metadata' || arrow.properties.showAsLeader === true) {
      const a = this.computeAnchorPoint();
      const { lng, lat } = this.surface.toMapCoords(a);
      const buffer = turf.buffer(
        {
          type: 'LineString',
          coordinates: [[lng, lat], ...this.vertices.map(({ lngLat }) => [lngLat.lng, lngLat.lat])],
        },
        4 / 2 ** (this.surface.map.getZoom() - 20),
        {
          units: 'feet',
        },
      )?.geometry as GeoJSON.Polygon;

      return buffer.coordinates[0].map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));
    }

    const bufferedPolygon = turf.buffer(
      {
        type: 'LineString',
        coordinates: this.vertices.map(({ lngLat }) => [lngLat.lng, lngLat.lat]),
      },
      4 / 2 ** (this.surface.map.getZoom() - 20),
      {
        units: 'feet',
      },
    )?.geometry as GeoJSON.Polygon;

    return bufferedPolygon.coordinates[0].map(([lng, lat]) => this.surface.toScreenCoords({ lng, lat }));
  };

  setOpacityWhileDragging = (ctx: CanvasRenderingContext2D) => {
    if (ctx.globalAlpha > opacityWhileDragging) {
      ctx.globalAlpha = opacityWhileDragging;
    }
  };

  drawBoundingBox = (ctx: CanvasRenderingContext2D) => {
    const arrow = this.getCurrentArrowObject();
    if (!arrow) return;
    const bbox = this.getBufferedPolygonCoords(arrow);

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
    ctx.strokeStyle = arrow.properties.color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(bbox[0].x, bbox[0].y);

    for (let i = 1; i < bbox.length; i++) {
      ctx.lineTo(bbox[i].x, bbox[i].y);
    }

    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  };

  drawBoundingBoxHitArea = (ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D, color: string) => {
    const arrow = this.getCurrentArrowObject();
    if (!arrow) return;
    const bbox = this.getBufferedPolygonCoords(arrow);

    ctx.fillStyle = color;
    ctx.beginPath();
    const p1 = bbox[0];

    if (this.isDragging()) {
      ctx.moveTo(p1.x + this.dragOffset.x, p1.y + this.dragOffset.y);

      for (let i = 1; i < bbox.length; i++) {
        const p = bbox[i];
        ctx.lineTo(p.x + this.dragOffset.x, p.y + this.dragOffset.y);
      }

      ctx.closePath();
      ctx.fill();
      return;
    }

    ctx.moveTo(p1.x, p1.y);

    for (let i = 1; i < bbox.length; i++) {
      const p = bbox[i];
      ctx.lineTo(p.x, p.y);
    }

    ctx.closePath();
    ctx.fill();
    return;
  };

  renderPointer = (ctx: CanvasRenderingContext2D) => {
    const arrow = this.getCurrentArrowObject();
    if (!arrow) return;
    const zoom = this.surface.map.getZoom();

    const p2 = this.vertices[1].point;

    ctx.save();
    ctx.strokeStyle = arrow.properties.color;
    ctx.globalAlpha = arrow.properties.strokeOpacity;
    ctx.lineWidth = arrow.properties.strokeWidth;
    ctx.setLineDash([]);
    ctx.beginPath();

    if (this.isDragging()) {
      ctx.translate(p2.x + this.dragOffset.x, p2.y + this.dragOffset.y);

      if (zoom <= ZOOM_THRESHOLD) {
        const s = getArrowScale(zoom);
        ctx.scale(s, s);
      }

      ctx.rotate(this.lineAngle);
      ctx.stroke(this.pointer);
      ctx.restore();
      return;
    }

    ctx.translate(p2.x, p2.y);

    if (zoom <= ZOOM_THRESHOLD) {
      const s = getArrowScale(zoom);
      ctx.scale(s, s);
    }

    ctx.rotate(this.lineAngle);
    ctx.stroke(this.pointer);
    ctx.restore();
    return;
  };

  renderLabel = (ctx: CanvasRenderingContext2D) => {
    const arrow = this.getCurrentArrowObject();
    if (!arrow) return;
    const { properties, title } = arrow;

    const { subtype } = properties;

    if (subtype !== 'metadata' && !properties.showTitle) return;

    const padding = 10;
    const fontSize = 14;
    const labelOffset = 2 * padding;
    ctx.font = `bold ${fontSize}px arial`;
    const textWidth = ctx.measureText(title).width;

    const p1 = this.vertices[0].point;
    const p2 = this.vertices[1].point;

    const lineLength = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const exceedsLength = textWidth + padding > lineLength;

    if (exceedsLength && subtype !== 'metadata' && !properties.labelAlwaysVisible) {
      return;
    }

    const p = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };

    const rawRotation = Math.atan2(p2.y - p1.y, p2.x - p1.x);

    let rotation: number = 0;
    if (rawRotation >= Math.PI / 2 && rawRotation <= Math.PI) {
      rotation = -1 * Math.PI + rawRotation;
    } else if (rawRotation > -1 * Math.PI && rawRotation < (-1 * Math.PI) / 2) {
      rotation = Math.PI + rawRotation;
    } else {
      rotation = rawRotation;
    }

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.translate(p.x, p.y);
    ctx.rotate(rotation);
    ctx.fillStyle = properties.color;
    ctx.beginPath();
    ctx.roundRect(-textWidth / 2 - padding / 2, -fontSize - labelOffset, textWidth + padding, fontSize + padding, [5]);
    ctx.fill();

    ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillText(title, 0, -fontSize / 2 - labelOffset - padding / 4);

    ctx.restore();
  };

  renderLabelOnLeader = (ctx: CanvasRenderingContext2D, a: Point, p1: Point) => {
    const arrow = this.getCurrentArrowObject();
    if (!arrow) return;
    const { properties, title, description } = arrow;
    const { subtype } = properties;

    const padding = 10;
    const fontSize = 14;
    const labelOffset = padding / 2;
    ctx.font = `bold ${fontSize}px arial`;

    if (subtype === 'metadata' && properties.font !== undefined) {
      ctx.font = `${fontSize}px ${properties.font}`;
    }

    const fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';

    let textLines: string[] = [];
    if (subtype === 'metadata' || properties.showTitle === true) {
      textLines.push(title);
    }

    if (subtype === 'metadata' || properties.showDescription) {
      const lines = description.trim() === '' ? ['Empty Text'] : description.split('\n');
      textLines = [...textLines, ...lines];
    }

    if (textLines.length === 0) {
      return;
    }

    const textHeight = fontSize * textLines.length;
    let textWidth: number = 0;

    for (let i = 0; i < textLines.length; i++) {
      const t = textLines[i];
      const w = ctx.measureText(t).width;
      textWidth = Math.max(textWidth, w);
    }

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = properties.color;
    ctx.translate(a.x, a.y);
    ctx.beginPath();

    if (a.x <= p1.x) {
      const x = -textWidth - labelOffset - padding;
      const y = -textHeight / 2 - padding / 2;
      const w = textWidth + padding;
      const h = textHeight + padding;

      ctx.textBaseline = 'top';
      ctx.textAlign = 'right';
      ctx.roundRect(x, y, w, h, [5]);
      ctx.fill();
      ctx.fillStyle = fillStyle;
      let yDiff = 0;
      for (let i = 0; i < textLines.length; i++) {
        ctx.fillText(textLines[i], -labelOffset - padding / 2, -textHeight / 2 + yDiff);
        yDiff += fontSize;
      }
    } else {
      const x = labelOffset;
      const y = -textHeight / 2 - padding / 2;
      const w = textWidth + padding;
      const h = textHeight + padding;
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.roundRect(x, y, w, h, [5]);
      ctx.fill();
      ctx.fillStyle = fillStyle;
      let yDiff = 0;
      for (let i = 0; i < textLines.length; i++) {
        ctx.fillText(textLines[i], labelOffset + padding / 2, -textHeight / 2 + yDiff);
        yDiff += fontSize;
      }
    }

    ctx.restore();
    return;
  };

  render(
    ctx: CanvasRenderingContext2D,
    _strokeColor?: string | undefined,
    _strokeWidth?: number | undefined,
    _isDashed?: boolean | undefined,
    _strokeOpacity?: number | undefined,
    _isBorderHidden?: boolean | undefined,
  ): void {
    if (this.vertices.length === 0) return;
    const arrow = this.getCurrentArrowObject();
    if (!arrow) return;
    const { properties } = arrow;
    const { subtype } = properties;

    const p1 = this.vertices[0].point;
    const p2 = this.vertices[1].point;
    this.lineAngle = this.computeLineAngle();

    const lineDash = properties.strokeStyle === 'dashed';

    ctx.globalAlpha = properties.strokeOpacity;
    ctx.strokeStyle = properties.color;
    ctx.lineWidth = properties.strokeWidth;
    lineDash && ctx.setLineDash([10, 4]);

    if (subtype === 'metadata' || properties.showAsLeader === true) {
      const a = this.computeAnchorPoint();

      if (this.isDragging()) {
        this.setOpacityWhileDragging(ctx);
        ctx.beginPath();
        ctx.moveTo(p2.x + this.dragOffset.x, p2.y + this.dragOffset.y);
        ctx.lineTo(p1.x + this.dragOffset.x, p1.y + this.dragOffset.y);
        ctx.lineTo(a.x + this.dragOffset.x, a.y + this.dragOffset.y);
        ctx.stroke();
        this.renderPointer(ctx);
        return;
      }

      ctx.beginPath();
      ctx.moveTo(p2.x, p2.y);
      ctx.lineTo(p1.x, p1.y);
      ctx.lineTo(a.x, a.y);
      ctx.stroke();

      this.renderPointer(ctx);
      this.drawBoundingBox(ctx);
      this.renderLabelOnLeader(ctx, a, p1);
      return;
    }

    if (this.isDragging()) {
      this.setOpacityWhileDragging(ctx);
      ctx.beginPath();
      ctx.moveTo(p1.x + this.dragOffset.x, p1.y + this.dragOffset.y);
      ctx.lineTo(p2.x + this.dragOffset.x, p2.y + this.dragOffset.y);
      ctx.stroke();
      this.renderPointer(ctx);
      return;
    }

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
    this.renderPointer(ctx);
    this.drawBoundingBox(ctx);
    this.renderLabel(ctx);
    return;
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    this.drawBoundingBoxHitArea(ctx, color);
  }
}
